import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
} from '@mui/material';
import {
  PictureAsPdf as PdfIcon,
  Search as SearchIcon,
  Print as PrintIcon,
  FilterList as FilterIcon,
} from '@mui/icons-material';
import { eventService } from '../services/api';
import { Event } from '../types';
import { divertilandiaColors } from '../theme/divertilandiaColors';
import ContractPDF from '../components/ContractPDF';
import { formatTime } from '../utils/dateUtils';

const ContractGenerator: React.FC = () => {
  const [events, setEvents] = useState<Event[]>([]);
  const [filteredEvents, setFilteredEvents] = useState<Event[]>([]);
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pdfDialogOpen, setPdfDialogOpen] = useState(false);
  
  // Estados para filtros
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    fetchEvents();
  }, []);

  // Efecto para filtrar eventos cuando cambian los filtros
  useEffect(() => {
    let filtered = events;

    // Filtrar por término de búsqueda (nombre del cliente)
    if (searchTerm.trim()) {
      filtered = filtered.filter(event =>
        event.contact_name?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filtrar por status
    if (statusFilter !== 'all') {
      filtered = filtered.filter(event => event.status === statusFilter);
    }

    setFilteredEvents(filtered);
  }, [events, searchTerm, statusFilter]);

  const fetchEvents = async () => {
    try {
      setLoading(true);
      setError(null);
      // Usar getAllComplete para obtener datos completos con imágenes para contratos
      const eventsData = await eventService.getAllComplete();
      // Filtrar solo eventos que no estén cancelados
      const validEvents = eventsData.filter(event => event.status !== 'cancelled');
      setEvents(validEvents);
      setFilteredEvents(validEvents);
    } catch (err) {
      setError('Error al cargar los eventos');
      console.error('Error fetching events:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateContract = (event: Event) => {
    setSelectedEvent(event);
    setPdfDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setPdfDialogOpen(false);
    setSelectedEvent(null);
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return divertilandiaColors.rainbow.green;
      case 'confirmed':
        return divertilandiaColors.rainbow.blue;
      case 'pending':
        return divertilandiaColors.rainbow.orange;
      case 'in_progress':
        return divertilandiaColors.rainbow.yellow;
      default:
        return divertilandiaColors.standard.neutral;
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'completed':
        return 'Completado';
      case 'confirmed':
        return 'Confirmado';
      case 'pending':
        return 'Pendiente';
      case 'in_progress':
        return 'En Progreso';
      default:
        return status;
    }
  };

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN',
    }).format(amount);
  };

  const formatDate = (dateString: string): string => {
    // Crear fecha local sin problemas de zona horaria
    const [year, month, day] = dateString.split('T')[0].split('-');
    const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    return date.toLocaleDateString('es-ES', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTime = (timeString: string): string => {
    if (!timeString) return '';
    // Si el tiempo tiene formato HH:MM:SS, extraer solo HH:MM
    const timeParts = timeString.split(':');
    if (timeParts.length >= 2) {
      return `${timeParts[0]}:${timeParts[1]}`;
    }
    return timeString;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box p={3}>
      {/* Header */}
      <Box display="flex" alignItems="center" mb={3}>
        <PdfIcon sx={{ mr: 2, color: divertilandiaColors.standard.primary, fontSize: 32 }} />
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: divertilandiaColors.standard.primary }}>
          Generador de Contratos
        </Typography>
      </Box>

      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Selecciona un evento para generar su contrato de servicio en formato PDF
      </Typography>

      {/* Filtros de búsqueda */}
      <Paper sx={{ p: 3, mb: 3, backgroundColor: '#f8f9fa' }}>
        <Box display="flex" alignItems="center" gap={2} mb={2}>
          <FilterIcon sx={{ color: divertilandiaColors.standard.primary }} />
          <Typography variant="h6" sx={{ fontWeight: 'bold', color: divertilandiaColors.standard.primary }}>
            Filtros de Búsqueda
          </Typography>
        </Box>
        
        <Grid container spacing={3} alignItems="center">
          {/* Barra de búsqueda por nombre */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Buscar por nombre del cliente"
              variant="outlined"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: divertilandiaColors.standard.primary }} />
                  </InputAdornment>
                ),
              }}
              placeholder="Ej: María González"
            />
          </Grid>

          {/* Filtro por status */}
          <Grid item xs={12} md={4}>
            <FormControl fullWidth variant="outlined">
              <InputLabel>Estado del Evento</InputLabel>
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                label="Estado del Evento"
              >
                <MenuItem value="all">Todos los estados</MenuItem>
                <MenuItem value="pending">Pendiente</MenuItem>
                <MenuItem value="confirmed">Confirmado</MenuItem>
                <MenuItem value="in_progress">En Progreso</MenuItem>
                <MenuItem value="completed">Completado</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Botón para limpiar filtros */}
          <Grid item xs={12} md={2}>
            <Button
              fullWidth
              variant="outlined"
              onClick={handleClearFilters}
              sx={{
                borderColor: divertilandiaColors.standard.primary,
                color: divertilandiaColors.standard.primary,
                '&:hover': {
                  borderColor: divertilandiaColors.rainbow.blue,
                  backgroundColor: `${divertilandiaColors.rainbow.blue}10`
                }
              }}
            >
              Limpiar
            </Button>
          </Grid>
        </Grid>

        {/* Información de resultados */}
        <Box mt={2}>
          <Typography variant="body2" color="text.secondary">
            Mostrando {filteredEvents.length} de {events.length} eventos
            {searchTerm && ` • Búsqueda: "${searchTerm}"`}
            {statusFilter !== 'all' && ` • Estado: ${getStatusText(statusFilter)}`}
          </Typography>
        </Box>
      </Paper>

      {/* Lista de eventos */}
      {filteredEvents.length === 0 && !loading ? (
        <Box textAlign="center" py={6}>
          <SearchIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No se encontraron eventos
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {searchTerm || statusFilter !== 'all' 
              ? 'Intenta ajustar los filtros de búsqueda'
              : 'No hay eventos disponibles para generar contratos'
            }
          </Typography>
          {(searchTerm || statusFilter !== 'all') && (
            <Button
              variant="outlined"
              onClick={handleClearFilters}
              sx={{
                borderColor: divertilandiaColors.standard.primary,
                color: divertilandiaColors.standard.primary,
              }}
            >
              Limpiar Filtros
            </Button>
          )}
        </Box>
      ) : (
        <Grid container spacing={3}>
          {filteredEvents.map((event) => (
          <Grid item xs={12} md={6} lg={4} key={event.id}>
            <Card sx={{ 
              '&:hover': { 
                boxShadow: 6,
                transform: 'translateY(-2px)',
                transition: 'all 0.3s ease-in-out'
              }
            }}>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', flex: 1 }}>
                    {event.contact_name}
                  </Typography>
                  <Box
                    sx={{
                      backgroundColor: getStatusColor(event.status),
                      color: 'white',
                      px: 1,
                      py: 0.5,
                      borderRadius: 1,
                      fontSize: '0.75rem',
                      fontWeight: 'bold',
                      ml: 1
                    }}
                  >
                    {getStatusText(event.status)}
                  </Box>
                </Box>

                <Typography variant="body2" color="text.secondary" gutterBottom>
                  <strong>Evento:</strong> {formatDate(event.event_date)}
                </Typography>

                <Typography variant="body2" color="text.secondary" gutterBottom>
                  <strong>Hora:</strong> {formatTime(event.start_time)}
                  {event.end_time && ` - ${formatTime(event.end_time)}`}
                </Typography>

                <Typography variant="body2" color="text.secondary" gutterBottom>
                  <strong>Paquete:</strong> {event.package?.name || 'Sin paquete'}
                </Typography>

                <Typography variant="body2" color="text.secondary" gutterBottom>
                  <strong>Teléfono:</strong> {event.contact_phone}
                </Typography>

                {event.child_gender && (
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    <strong>Festejado:</strong> {event.child_gender === 'male' ? 'Niño' : 'Niña'}
                  </Typography>
                )}

                <Typography variant="body2" color="text.secondary" gutterBottom>
                  <strong>Precio:</strong> {formatCurrency(Number(event.package?.price || 0))}
                </Typography>

                <Button
                  variant="contained"
                  fullWidth
                  sx={{ 
                    mt: 2,
                    backgroundColor: divertilandiaColors.standard.primary,
                    '&:hover': {
                      backgroundColor: divertilandiaColors.rainbow.blue
                    }
                  }}
                  startIcon={<PrintIcon />}
                  onClick={() => handleGenerateContract(event)}
                >
                  Generar Contrato
                </Button>
              </CardContent>
            </Card>
          </Grid>
          ))}
        </Grid>
      )}


      {/* Dialog para mostrar el PDF */}
      <Dialog
        open={pdfDialogOpen}
        onClose={handleCloseDialog}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            minHeight: '80vh'
          }
        }}
      >
        <DialogTitle>
          <Box display="flex" alignItems="center">
            <PdfIcon sx={{ mr: 1, color: divertilandiaColors.standard.primary }} />
            Contrato de Servicio - {selectedEvent?.contact_name}
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedEvent && (
            <ContractPDF event={selectedEvent} />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ContractGenerator;
